import { Handle, Position } from '@xyflow/react';
import { Stack, Select, NumberInput } from '@mantine/core';
import { IconHeadset } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { transferToAgentWidgetCore, type TransferToAgentConfig } from '@legacy-shared/widgets/core/transferToAgent';
import { graphql } from '../../lib/graphql';
import { useOrg } from '../../hooks/use-org';

export { type TransferToAgentConfig } from '@legacy-shared/widgets/core/transferToAgent';

const OrganizationMembersSubscription = graphql(`
  subscription WidgetOrganizationMembers($organizationId: Uuid!) {
    organizationMembers(where: { organizationId: { _eq: $organizationId }, status: { _eq: "active" } }) {
      id
      email
      extensionNumber
    }
  }
`);

function TransferToAgentNode({ id, data, selected }: WidgetNodeProps<TransferToAgentConfig>) {
  return (
    <WidgetNodeShell
      icon={IconHeadset}
      title={data.label || 'Transfer to Agent'}
      subtitle={data.config.memberName || 'No agent selected'}
      configured={!!data.config.memberId}
      errors={data.errors}
      selected={selected}
      color="violet"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="answered" label="Answered" offset="50%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="timeout" label="Timeout / Busy" offset="33%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="offline" label="Offline" offset="67%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function TransferToAgentConfigPanel({ config, onChange }: ConfigPanelProps<TransferToAgentConfig>) {
  const { organizationId } = useOrg();

  const [{ data }] = useSubscription({
    query: OrganizationMembersSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const members = data?.organizationMembers;

  const agentMembers = (members ?? [])
    .filter((m) => m.extensionNumber)
    .map((m) => ({
      value: m.id,
      label: `${m.email} (ext. ${m.extensionNumber})`,
    }));

  return (
    <Stack gap="md">
      <Select
        label="Agent"
        description="Select the agent to route the call to"
        data={agentMembers}
        value={config.memberId || null}
        onChange={(v) => {
          const member = members?.find((m) => m.id === v);
          onChange({
            ...config,
            memberId: v ?? '',
            memberName: member ? member.email : '',
          });
        }}
        searchable
      />
      <NumberInput
        label="Ring Timeout (seconds)"
        description="How long to ring the agent before falling back"
        value={config.ringTimeoutSecs}
        onChange={(v) => onChange({ ...config, ringTimeoutSecs: typeof v === 'number' ? v : 30 })}
        min={5}
        max={120}
      />
    </Stack>
  );
}

export const transferToAgentWidget: WidgetDefinition<TransferToAgentConfig> = {
  ...transferToAgentWidgetCore,
  icon: 'IconHeadset',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'answered', type: 'success', label: 'Answered', position: 'bottom' },
      { id: 'timeout', type: 'timeout', label: 'Timeout / Busy', position: 'right' },
      { id: 'offline', type: 'failure', label: 'Offline', position: 'right' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: TransferToAgentNode,
    configPanel: TransferToAgentConfigPanel,
    preview: (c) => c.memberName || 'No agent selected',
  },

  validation: {
    ...transferToAgentWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
